import React, { useState, useEffect, useCallback } from "react";
import { useApiClient } from "../../../data/clients/ApiClient";
import { useLogoRepository } from "../../../data/repositories/LogoRepository";
import { JetSportBox } from "../../components/BoxView/JetSportBox";
import { BoxHeader2 } from "../../components/BoxView/BoxHeader2";
import { BoxHeaderTypography } from "../../components/Typographies/BoxHeaderTypography";
import { BoxContent } from "../../components/BoxView/BoxContent";
import { PrimaryButton } from "../../components/Buttons/PrimaryButton";
import { SecondaryButton } from "../../components/Buttons/SecondaryButton";
import { useSuccessToast, useErrorToast } from "../../components/Toast/Toast";
import LoadingElement from "../../components/loading-element/loading-element";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlay,
  faSearch,
  faFilter,
  faCheck,
  faCheckDouble,
  faExclamationTriangle,
  faRotate,
  faUpload,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { useConfirmationDialog } from "../../components/Dialogs/confirmation-dialog";
import { PageQuery } from "../../../utils/models/pageQuery";
import { SortDirection } from "../../components/GenericTable/Models/sortDirection";
import { LogoImage } from "../../components/Logo/LogoImage";
import {
  Checkbox,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Pagination,
} from "@mui/material";
import { useRepositoriesContext } from "../../../contexts/RepositoriesContext";
import { useCustomerAndVendors } from "../../shared/hooks/useCustomerAndVendors";

const PAGE_SIZE = 12;

// Classification options based on existing codebase patterns
const CLASSIFICATION_OPTIONS = [
  { value: "", label: "Alle klassifikationer" },
  { value: "3", label: "Standard (3)" },
  { value: "11", label: "Kædelogoer (11)" },
  { value: "6", label: "Talpakker (6)" },
  { value: "2", label: "Navne (2)" },
];

export function LogoImportScreen() {
  const apiClient = useApiClient();
  const { organizationRepository } = useRepositoriesContext();
  const logoRepository = useLogoRepository();

  // Input filter states (what user is typing)
  const [searchTermInput, setSearchTermInput] = useState("");
  const [organizationSearchInput, setOrganizationSearchInput] = useState("");
  const [classificationInput, setClassificationInput] = useState("");
  const [methodInput, setMethodInput] = useState("");
  const [customerIdInput, setCustomerIdInput] = useState<number | "">("");

  // Applied filter states (used for actual query)
  const [appliedSearchTerm, setAppliedSearchTerm] = useState("");
  const [appliedOrganizationSearch, setAppliedOrganizationSearch] =
    useState("");
  const [appliedClassification, setAppliedClassification] = useState("");
  const [appliedMethod, setAppliedMethod] = useState("");
  const [appliedCustomerId, setAppliedCustomerId] = useState<number | "">("");
  const [pageIndex, setPageIndex] = useState(1);

  // Data states
  const [selectedLogos, setSelectedLogos] = useState<Set<string>>(new Set());
  const [isImporting, setIsImporting] = useState(false);
  const [isUpdatingOrgs, setIsUpdatingOrgs] = useState(false);
  const [isLoadingAllSerials, setIsLoadingAllSerials] = useState(false);
  const [totalMatchingCount, setTotalMatchingCount] = useState<number | null>(
    null,
  );
  const [importResult, setImportResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  // Apply filters when Search button is clicked
  const handleSearch = () => {
    setAppliedSearchTerm(searchTermInput);
    setAppliedOrganizationSearch(organizationSearchInput);
    setAppliedClassification(classificationInput);
    setAppliedMethod(methodInput);
    setAppliedCustomerId(customerIdInput);
    setPageIndex(1); // Reset to first page when searching
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchTermInput("");
    setOrganizationSearchInput("");
    setClassificationInput("");
    setMethodInput("");
    setCustomerIdInput("");
    setAppliedSearchTerm("");
    setAppliedOrganizationSearch("");
    setAppliedClassification("");
    setAppliedMethod("");
    setAppliedCustomerId("");
    setPageIndex(1);
    setTotalMatchingCount(null);
  };

  // Select all matching logos (fetches all serials from backend)
  const handleSelectAllMatching = async () => {
    setIsLoadingAllSerials(true);
    try {
      const response = await apiClient.getLogoSerialsForImport({
        searchTerm: appliedSearchTerm || undefined,
        organizationName: appliedOrganizationSearch || undefined,
        classification: appliedClassification || undefined,
        customerId: appliedCustomerId || undefined,
        method: appliedMethod || undefined,
      });

      if (response.serials && response.serials.length > 0) {
        const newSelection = new Set(response.serials);
        setSelectedLogos(newSelection);
        setTotalMatchingCount(response.count);
        useSuccessToast(`${response.count} logoer valgt`);
      } else {
        useErrorToast("Ingen logoer fundet med de valgte filtre");
      }
    } catch (error) {
      console.error("Error fetching all serials:", error);
      useErrorToast("Fejl ved hentning af logoer");
    } finally {
      setIsLoadingAllSerials(false);
    }
  };

  // Build the query using APPLIED filters only
  const buildQuery = useCallback((): PageQuery => {
    const filters: { field: string; value: string }[] = [];

    if (appliedClassification) {
      filters.push({ field: "classification", value: appliedClassification });
    }
    if (appliedMethod) {
      filters.push({ field: "method", value: appliedMethod });
    }
    if (appliedCustomerId) {
      filters.push({ field: "customerId", value: String(appliedCustomerId) });
    }
    if (appliedOrganizationSearch.trim()) {
      filters.push({
        field: "organizationName",
        value: appliedOrganizationSearch.trim(),
      });
    }

    return {
      searchTerm: appliedSearchTerm,
      filterBy: filters,
      sortBy: { field: "createdAtUtc", sortDirection: SortDirection.DESC },
      page: { size: PAGE_SIZE, index: pageIndex },
    };
  }, [
    appliedSearchTerm,
    appliedOrganizationSearch,
    appliedClassification,
    appliedMethod,
    appliedCustomerId,
    pageIndex,
  ]);

  // Fetch logos with filters
  const {
    data: logoData,
    isLoading,
    isError,
  } = logoRepository.useAllLogos(buildQuery());

  // Get customers for dropdown
  const { customers, isLoading: isLoadingCustomers } = useCustomerAndVendors(
    organizationRepository,
  );

  // Get the queue counts for status display
  const [queueCounts, setQueueCounts] = useState<
    { queueName: string; count: number }[]
  >([]);

  const fetchQueueCounts = async () => {
    try {
      const counts = await logoRepository.getImportLogosQueueCounts();
      setQueueCounts(counts);
    } catch (error) {
      console.error("Error fetching queue counts:", error);
    }
  };

  useEffect(() => {
    fetchQueueCounts();
    const interval = setInterval(fetchQueueCounts, 10000); // Refresh every 10 seconds
    return () => clearInterval(interval);
  }, []);

  // Handle select all on current page
  const handleSelectAll = () => {
    if (!logoData?.results) return;

    const currentPageSerials = logoData.results
      .filter(
        (logo) => logo.product?.sku && !logo.product.sku.startsWith("UPLOAD_"),
      )
      .map((logo) => logo.product!.sku);

    const allSelected = currentPageSerials.every((sku) =>
      selectedLogos.has(sku),
    );

    if (allSelected) {
      // Deselect all on current page
      const newSelection = new Set(selectedLogos);
      currentPageSerials.forEach((sku) => newSelection.delete(sku));
      setSelectedLogos(newSelection);
    } else {
      // Select all on current page
      const newSelection = new Set(selectedLogos);
      currentPageSerials.forEach((sku) => newSelection.add(sku));
      setSelectedLogos(newSelection);
    }
  };

  // Handle individual logo selection
  const handleLogoSelect = (sku: string) => {
    const newSelection = new Set(selectedLogos);
    if (newSelection.has(sku)) {
      newSelection.delete(sku);
    } else {
      newSelection.add(sku);
    }
    setSelectedLogos(newSelection);
  };

  // Handle import
  const handleImport = async () => {
    if (selectedLogos.size === 0) {
      useErrorToast("Vælg mindst ét logo at importere");
      return;
    }

    setIsImporting(true);
    setImportResult(null);

    try {
      const serialNumbers = Array.from(selectedLogos);
      const result = await apiClient.bulkLogoImport(serialNumbers);

      if (result.success) {
        useSuccessToast(`${result.queuedCount} logoer sat i import-kø`);
        setImportResult({ success: true, message: result.message });
        setSelectedLogos(new Set()); // Clear selection after successful import
        fetchQueueCounts(); // Refresh queue counts
      } else {
        useErrorToast("Import fejlede");
        setImportResult({ success: false, message: "Import fejlede" });
      }
    } catch (error) {
      console.error("Import error:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Der opstod en fejl under import";
      useErrorToast(errorMessage);
      setImportResult({ success: false, message: errorMessage });
    } finally {
      setIsImporting(false);
    }
  };

  // Handle organization update
  const handleOrganizationUpdate = async () => {
    if (selectedLogos.size === 0) {
      useErrorToast("Vælg mindst ét logo at opdatere");
      return;
    }

    setIsUpdatingOrgs(true);
    setImportResult(null);

    try {
      const serialNumbers = Array.from(selectedLogos);
      const result = await apiClient.bulkLogoOrganizationUpdate(serialNumbers);

      if (result.success) {
        useSuccessToast(
          `${result.queuedCount} logoer sat i organisations-opdaterings-kø`,
        );
        setImportResult({ success: true, message: result.message });
        setSelectedLogos(new Set()); // Clear selection after successful queue
        fetchQueueCounts(); // Refresh queue counts
      } else {
        useErrorToast("Organisations-opdatering fejlede");
        setImportResult({
          success: false,
          message: "Organisations-opdatering fejlede",
        });
      }
    } catch (error) {
      console.error("Organization update error:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Der opstod en fejl under organisations-opdatering";
      useErrorToast(errorMessage);
      setImportResult({ success: false, message: errorMessage });
    } finally {
      setIsUpdatingOrgs(false);
    }
  };

  // Confirmation dialog for import
  const confirmationDialog = useConfirmationDialog(
    `Er du sikker på, at du vil starte import af ${selectedLogos.size} logoer?\n\nDette vil opdatere data fra Navision for de valgte logoer.`,
    handleImport,
  );

  // Confirmation dialog for organization update
  const orgUpdateConfirmationDialog = useConfirmationDialog(
    `Er du sikker på, at du vil opdatere organisationer for ${selectedLogos.size} logoer?\n\nDette vil opdatere hvilke kunder der har adgang til de valgte logoer baseret på Navision data.`,
    handleOrganizationUpdate,
  );

  // Calculate if all on current page are selected
  const allOnPageSelected =
    logoData?.results
      ?.filter(
        (logo) => logo.product?.sku && !logo.product.sku.startsWith("UPLOAD_"),
      )
      ?.every((logo) => selectedLogos.has(logo.product!.sku)) ?? false;

  const importQueueCount =
    queueCounts.find((q) => q.queueName === "import-logos-queue")?.count ?? 0;

  return (
    <div className="p-8 space-y-6">
      {confirmationDialog.component}
      {orgUpdateConfirmationDialog.component}

      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-800 mb-2">
          Admin: Logo Import
        </h1>
        <p className="text-sm text-gray-600">
          Vælg logoer der skal genimporteres fra Navision. Du kan filtrere på
          kunde, klassifikation, organisationsnavn eller søge efter specifikke
          logoer.
        </p>
      </div>

      {/* Queue Status */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 flex items-center gap-4">
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faUpload} className="text-slate-500" />
          <span className="text-sm font-medium text-slate-700">Import kø:</span>
          <span
            className={`text-sm font-bold ${
              importQueueCount > 0 ? "text-amber-600" : "text-green-600"
            }`}
          >
            {importQueueCount} ventende
          </span>
        </div>
        <SecondaryButton width="sm" onClick={fetchQueueCounts}>
          <FontAwesomeIcon icon={faRotate} className="mr-2" />
          Opdater
        </SecondaryButton>
      </div>

      {/* Filters Section */}
      <JetSportBox>
        <BoxHeader2>
          <BoxHeaderTypography>
            <FontAwesomeIcon icon={faFilter} className="mr-2" />
            Filtre
          </BoxHeaderTypography>
        </BoxHeader2>
        <BoxContent className="!min-h-0 py-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {/* Search */}
            <TextField
              label="Søg (serienummer, titel)"
              variant="outlined"
              size="small"
              value={searchTermInput}
              onChange={(e) => setSearchTermInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              InputProps={{
                startAdornment: (
                  <FontAwesomeIcon
                    icon={faSearch}
                    className="mr-2 text-gray-400"
                  />
                ),
              }}
            />

            {/* Organization Name Search */}
            <TextField
              label="Organisation navn indeholder..."
              variant="outlined"
              size="small"
              value={organizationSearchInput}
              onChange={(e) => setOrganizationSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              placeholder='F.eks. "Sport 24"'
              InputProps={{
                startAdornment: (
                  <FontAwesomeIcon
                    icon={faSearch}
                    className="mr-2 text-gray-400"
                  />
                ),
              }}
            />

            {/* Classification */}
            <FormControl size="small">
              <InputLabel>Klassifikation</InputLabel>
              <Select
                value={classificationInput}
                label="Klassifikation"
                onChange={(e) => setClassificationInput(e.target.value)}
              >
                {CLASSIFICATION_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Transfer Method Search */}
            <TextField
              label="Transfer Metode"
              variant="outlined"
              size="small"
              value={methodInput}
              onChange={(e) => setMethodInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />

            {/* Customer */}
            <FormControl size="small">
              <InputLabel>Kunde</InputLabel>
              <Select
                value={customerIdInput}
                label="Kunde"
                onChange={(e) =>
                  setCustomerIdInput(e.target.value as number | "")
                }
                disabled={isLoadingCustomers}
              >
                <MenuItem value="">Alle kunder</MenuItem>
                {customers?.map((customer) => (
                  <MenuItem key={customer.id} value={customer.id}>
                    {customer.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Search Button */}
            <PrimaryButton onClick={handleSearch}>
              <FontAwesomeIcon icon={faSearch} className="mr-2" />
              Søg
            </PrimaryButton>

            {/* Clear Filters */}
            <SecondaryButton width="sm" onClick={handleClearFilters}>
              Ryd filtre
            </SecondaryButton>
          </div>
        </BoxContent>
      </JetSportBox>

      {/* Selection Summary & Import Button */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-4">
          <span className="text-sm font-medium text-blue-800">
            {selectedLogos.size} logoer valgt
            {totalMatchingCount !== null &&
              totalMatchingCount !== selectedLogos.size && (
                <span className="text-blue-600">
                  {" "}
                  (af {totalMatchingCount} matchende)
                </span>
              )}
          </span>

          {/* Select All Matching Button */}
          <PrimaryButton
            onClick={handleSelectAllMatching}
            disabled={isLoadingAllSerials}
          >
            {isLoadingAllSerials ? (
              <>
                <FontAwesomeIcon
                  icon={faRotate}
                  className="mr-2 animate-spin"
                />
                Henter...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faCheckDouble} className="mr-2" />
                Vælg alle matchende
              </>
            )}
          </PrimaryButton>

          {selectedLogos.size > 0 && (
            <SecondaryButton
              width="sm"
              onClick={() => setSelectedLogos(new Set())}
            >
              Ryd valg
            </SecondaryButton>
          )}
        </div>
        <div className="flex gap-2">
          <SecondaryButton
            onClick={orgUpdateConfirmationDialog.open}
            disabled={selectedLogos.size === 0 || isUpdatingOrgs || isImporting}
          >
            {isUpdatingOrgs ? (
              <>
                <FontAwesomeIcon
                  icon={faRotate}
                  className="mr-2 animate-spin"
                />
                Opdaterer...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faUsers} className="mr-2" />
                Opdater Organisationer ({selectedLogos.size})
              </>
            )}
          </SecondaryButton>
          <PrimaryButton
            onClick={confirmationDialog.open}
            disabled={selectedLogos.size === 0 || isImporting || isUpdatingOrgs}
          >
            {isImporting ? (
              <>
                <FontAwesomeIcon
                  icon={faRotate}
                  className="mr-2 animate-spin"
                />
                Importerer...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faPlay} className="mr-2" />
                Start Import ({selectedLogos.size})
              </>
            )}
          </PrimaryButton>
        </div>
      </div>

      {/* Import Result */}
      {importResult && (
        <div
          className={`border rounded-lg p-4 flex items-center gap-3 ${
            importResult.success
              ? "bg-green-50 border-green-200"
              : "bg-red-50 border-red-200"
          }`}
        >
          <FontAwesomeIcon
            icon={importResult.success ? faCheck : faExclamationTriangle}
            className={importResult.success ? "text-green-500" : "text-red-500"}
          />
          <span
            className={importResult.success ? "text-green-700" : "text-red-700"}
          >
            {importResult.message}
          </span>
        </div>
      )}

      {/* Logos Table */}
      <JetSportBox>
        <BoxHeader2>
          <div className="flex items-center justify-between w-full">
            <BoxHeaderTypography>
              Logoer ({logoData?.results?.length ?? 0} på denne side, side{" "}
              {logoData?.pageIndex ?? 1} af {logoData?.pageCount ?? 1})
            </BoxHeaderTypography>
            <div className="flex items-center gap-2">
              <Checkbox
                checked={
                  allOnPageSelected &&
                  logoData?.results &&
                  logoData.results.length > 0
                }
                indeterminate={selectedLogos.size > 0 && !allOnPageSelected}
                onChange={handleSelectAll}
                size="small"
              />
              <span className="text-sm text-gray-600">Vælg alle på siden</span>
            </div>
          </div>
        </BoxHeader2>
        <BoxContent className="!min-h-0 py-0 px-0">
          {isLoading ? (
            <div className="h-64 relative">
              <LoadingElement />
            </div>
          ) : isError ? (
            <div className="p-8 text-center text-red-500">
              <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
              Fejl ved hentning af logoer
            </div>
          ) : (
            <>
              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="py-3 px-4 text-left w-12"></th>
                      <th className="py-3 px-4 text-left w-20">Billede</th>
                      <th className="py-3 px-4 text-left">Serienummer</th>
                      <th className="py-3 px-4 text-left">Titel</th>
                      <th className="py-3 px-4 text-left">Kunde</th>
                      <th className="py-3 px-4 text-left">Type</th>
                      <th className="py-3 px-4 text-left">Størrelse</th>
                      <th className="py-3 px-4 text-left">Klassifikation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logoData?.results?.map((logo) => {
                      const sku = logo.product?.sku;
                      const isUploadLogo = sku?.startsWith("UPLOAD_");
                      const canSelect = sku && !isUploadLogo;
                      const isSelected = sku ? selectedLogos.has(sku) : false;

                      return (
                        <tr
                          key={logo.id}
                          className={`border-b hover:bg-gray-50 ${
                            isSelected ? "bg-blue-50" : ""
                          } ${!canSelect ? "opacity-50" : "cursor-pointer"}`}
                          onClick={() => canSelect && handleLogoSelect(sku)}
                        >
                          <td className="py-2 px-4">
                            {canSelect && (
                              <Checkbox
                                checked={isSelected}
                                onChange={() => handleLogoSelect(sku)}
                                onClick={(e) => e.stopPropagation()}
                                size="small"
                              />
                            )}
                          </td>
                          <td className="py-2 px-4">
                            <div className="w-12 h-12 flex items-center justify-center">
                              <LogoImage
                                logo={logo}
                                useHighRes={false}
                                className="max-w-12 max-h-12 object-contain"
                              />
                            </div>
                          </td>
                          <td className="py-2 px-4 font-mono text-xs">
                            {isUploadLogo ? (
                              <span className="text-gray-400 italic">
                                Upload
                              </span>
                            ) : (
                              sku || "-"
                            )}
                          </td>
                          <td className="py-2 px-4">{logo.title || "-"}</td>
                          <td className="py-2 px-4 text-xs">
                            {logo.customer?.name || "-"}
                          </td>
                          <td className="py-2 px-4 text-xs">
                            {logo.transferMethod?.type || "-"}
                          </td>
                          <td className="py-2 px-4 text-xs">
                            {logo.widthMm}x{logo.heightMm}mm
                          </td>
                          <td className="py-2 px-4 text-xs">
                            {logo.Classification ?? "-"}
                          </td>
                        </tr>
                      );
                    })}
                    {(!logoData?.results || logoData.results.length === 0) && (
                      <tr>
                        <td
                          colSpan={8}
                          className="py-8 text-center text-gray-500"
                        >
                          Ingen logoer fundet med de valgte filtre
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {logoData && logoData.pageCount > 1 && (
                <div className="py-4 px-4 flex justify-center border-t">
                  <Pagination
                    count={logoData.pageCount}
                    page={pageIndex}
                    onChange={(_, page) => setPageIndex(page)}
                    color="primary"
                  />
                </div>
              )}
            </>
          )}
        </BoxContent>
      </JetSportBox>
    </div>
  );
}

export default LogoImportScreen;
